import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Flex,
  Badge,
  SimpleGrid,
  Image,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useBreakpointValue,
} from "@chakra-ui/react";
import { MdPlayCircle } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { GOLD_TEXT } from "constants/brandColors";
import { toLocalTime } from "utils/time";
import { getTranslatedField, localeLabel } from "utils/translations";
import MediaLightbox from "./MediaLightbox";

const STATUS_COLORS = { DRAFT: "gray", PUBLISHED: "green", ARCHIVED: "orange" };

// Read-only detail view for a news item — no editing, just a full look at
// every field plus its images/videos, separate from the Edit modal.
function ViewNewsModal({ news, onClose }) {
  const { t } = useTranslation();
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const [lightboxMedia, setLightboxMedia] = useState(null);

  if (!news) return null;

  const Field = ({ label, children }) => (
    <Box mb="14px">
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
        {label}
      </Text>
      <Box fontSize="sm" color="gray.800">{children}</Box>
    </Box>
  );

  const title = getTranslatedField(news.translations, "en", "title") || getTranslatedField(news.translations, "ar", "title");

  // Read-only view: rather than fetching the active-languages list (which
  // would need its own permission), just show a tab for whichever locales
  // this particular news item actually has translations for.
  const presentLocaleCodes = (news.translations || []).map((tr) => tr.locale);

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size={modalSize} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700" noOfLines={2}>
              {title || t("Untitled")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex gap={2} mb="16px" flexWrap="wrap">
              <Badge colorScheme={STATUS_COLORS[news.status] || "gray"} variant="solid">{t(news.status || "DRAFT")}</Badge>
              {news.breaking_news && <Badge colorScheme="red" variant="solid">{t("Breaking")}</Badge>}
              {news.priority && <Badge colorScheme="orange" variant="solid">{t("Priority")}</Badge>}
              {(news.tags || []).map((tag) => (
                <Badge key={tag.id} colorScheme="gray" variant="subtle">{getTranslatedField(tag.translations, "en")}</Badge>
              ))}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Field label={t("Category")}>{getTranslatedField(news.category?.translations, "en") || "—"}</Field>
              <Field label={t("Sub Category")}>{news.subcategory ? getTranslatedField(news.subcategory.translations, "en") : "—"}</Field>
              <Field label={t("Published By")}>{news.status === "PUBLISHED" ? (news.publisher_by || "—") : t("Not published")}</Field>
              <Field label={t("Published Date")}>{news.publishedAt ? toLocalTime(news.publishedAt) : "—"}</Field>
              <Field label={t("Views")}>{news.views ?? 0}</Field>
              <Field label={t("Created")}>{news.createdAt ? toLocalTime(news.createdAt) : "—"}</Field>
              <Field label={t("Created By")}>
                {news.createdByUser ? `${news.createdByUser.firstName} ${news.createdByUser.lastName} (${news.createdByUser.email})` : "—"}
              </Field>
              <Field label={t("Last Updated")}>{news.updatedAt ? toLocalTime(news.updatedAt) : "—"}</Field>
              <Field label={t("Last Updated By")}>
                {news.updatedByUser ? `${news.updatedByUser.firstName} ${news.updatedByUser.lastName} (${news.updatedByUser.email})` : "—"}
              </Field>
            </SimpleGrid>

            <Divider my="16px" />

            <Tabs colorScheme="gray" variant="enclosed">
              <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap" sx={{ scrollbarWidth: "thin" }}>
                {presentLocaleCodes.map((code) => (
                  <Tab key={code} whiteSpace="nowrap" flexShrink={0} _selected={{ color: GOLD_TEXT, borderColor: "gray.200", borderBottomColor: "white", fontWeight: "700" }}>
                    {localeLabel(code)}
                  </Tab>
                ))}
              </TabList>
              <TabPanels borderWidth="1px" borderTop="none" borderColor="gray.200" borderBottomRadius="md" p="12px">
                {presentLocaleCodes.map((code) => (
                  <TabPanel key={code} p="4px">
                    <Field label={t("Header")}>{getTranslatedField(news.translations, code, "header") || "—"}</Field>
                    <Field label={t("Text")}>
                      <Box
                        dir={code === "ar" ? "rtl" : "ltr"}
                        sx={{
                          "p": { margin: "0 0 8px" },
                          "ul, ol": { paddingInlineStart: "24px", margin: "0 0 8px" },
                          "blockquote": { borderInlineStart: "3px solid", borderColor: "gray.300", paddingInlineStart: "12px", color: "gray.600" },
                          "img": { maxWidth: "100%", borderRadius: "4px" },
                        }}
                        // `text` is HTML already sanitized server-side (sanitize-html
                        // allow-list) before it was stored, so it's safe to render as-is.
                        dangerouslySetInnerHTML={{ __html: getTranslatedField(news.translations, code, "text") || "" }}
                      />
                    </Field>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>

            <Divider my="16px" />

            <Text fontSize="md" fontWeight="bold" mb="2">
              {t("Images")} {news.newsimages?.length ? `(${news.newsimages.length})` : ""}
            </Text>
            {!news.newsimages || news.newsimages.length === 0 ? (
              <Text fontSize="sm" color="gray.500" mb="4">{t("No images.")}</Text>
            ) : (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb="4">
                {news.newsimages.map((img) => (
                  <Box
                    key={img.id}
                    borderRadius="md"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.200"
                    cursor="pointer"
                    onClick={() => setLightboxMedia({ type: "image", src: img.image_url, alt: img.caption })}
                    _hover={{ opacity: 0.85 }}
                  >
                    <Image src={img.image_url} alt={img.caption || "news image"} objectFit="cover" h="90px" w="100%" />
                  </Box>
                ))}
              </SimpleGrid>
            )}

            <Text fontSize="md" fontWeight="bold" mb="2">
              {t("Videos")} {news.newsvideos?.length ? `(${news.newsvideos.length})` : ""}
            </Text>
            {!news.newsvideos || news.newsvideos.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{t("No videos.")}</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {news.newsvideos.map((vid) => (
                  <Flex
                    key={vid.id}
                    align="center"
                    gap={2}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p="2"
                    cursor="pointer"
                    onClick={() => setLightboxMedia({ type: "video", src: vid.video_url, alt: vid.caption })}
                    _hover={{ bg: "gray.50" }}
                  >
                    <MdPlayCircle size={20} color={GOLD_TEXT} />
                    <Text fontSize="sm" noOfLines={1}>{vid.caption || t("Video")}</Text>
                  </Flex>
                ))}
              </SimpleGrid>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <MediaLightbox media={lightboxMedia} onClose={() => setLightboxMedia(null)} />
    </>
  );
}

export default ViewNewsModal;
